/** Values that are omitted from the query string (`null` and `undefined` only). */
export type NavigationHrefQueryParamValue = string | number | boolean | null | undefined;

export interface BuildNavigationHrefOptions {
  /**
   * Query parameters. Entries with `null` or `undefined` values are skipped;
   * other values are stringified (`true` / `false` for booleans).
   */
  queryParams?: Record<string, NavigationHrefQueryParamValue>;
  /** Fragment id (no `#`). Omit or pass `undefined` to clear the hash. */
  stepHash?: string;
}

/**
 * Builds an `href` from a base URL, optional query params, and an optional hash.
 */
export const buildNavigationHref = (
  baseHref: string,
  options: BuildNavigationHrefOptions = {},
): string => {
  const { queryParams, stepHash } = options;

  const isRelativeBase = (() => {
    try {
      new URL(baseHref);
      return false;
    } catch {
      return true;
    }
  })();

  let url: URL;
  try {
    url = new URL(baseHref);
  } catch {
    const baseUrl =
      typeof globalThis.window !== 'undefined' && globalThis.window.location?.href
        ? globalThis.window.location.href
        : 'http://localhost';
    url = new URL(baseHref, baseUrl);
  }

  if (queryParams) {
    for (const [key, value] of Object.entries(queryParams)) {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }
  url.hash = stepHash ?? '';

  if (isRelativeBase) {
    return `${url.pathname}${url.search}${url.hash}`;
  }
  return url.href;
};
